<script setup lang="ts">
import { computed } from 'vue';

export type ProgressBarStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type ProgressBarSize = 'sm' | 'md';

interface Props {
  step: ProgressBarStep;
  /** Denominator for progress. Defaults to 5 for legacy 5-step flows; pass 8 for checkout. */
  totalSteps?: number;
  /** sm = 6px, md = 8px (used by both desktop and mobile secondary headers per Figma) */
  size?: ProgressBarSize;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  totalSteps: 5,
  size: 'sm',
  ariaLabel: undefined,
});

// If callers pass step > totalSteps (e.g. step 6–8 with the legacy default of 5),
// raise the denominator so fill/ARIA never exceed 100% / valuemax.
const resolvedTotalSteps = computed(() => Math.max(props.totalSteps, props.step));
const percentage = computed(() =>
  Math.min(100, Math.round((props.step / resolvedTotalSteps.value) * 100))
);
</script>

<template>
  <div
    class="pj-progress-bar"
    :class="`pj-progress-bar--${size}`"
    role="progressbar"
    :aria-valuenow="step"
    :aria-valuemin="1"
    :aria-valuemax="resolvedTotalSteps"
    :aria-label="ariaLabel ?? `Step ${step} of ${resolvedTotalSteps}`"
  >
    <div class="pj-progress-bar__fill" :style="{ width: `${percentage}%` }" />
  </div>
</template>

<style lang="scss" scoped>
.pj-progress-bar {
  width: 100%;
  background: var(--pj-color-surface-track, #f4f4f4);
  border-radius: 0;
  overflow: hidden;

  &--sm { height: 6px; }
  &--md { height: 8px; }
}

.pj-progress-bar__fill {
  height: 100%;
  border-radius: 0;
  background: linear-gradient(
    to right,
    var(--pj-color-progress-fill-start, #42bf00),
    var(--pj-color-progress-fill-end, #007c3b)
  );
  transition: width var(--pj-motion-duration-base) var(--pj-motion-easing-standard);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}
</style>
